import { createContext, FC, ReactNode, useContext, useMemo, useState } from 'react';
import { CourseWithProgress } from '@teachur/http-client';

export type CourseStatus = 'all' | 'notStarted' | 'inProgress' | 'completed';
export type CourseStatusMap = Record<CourseStatus, CourseWithProgress[]>;

export interface StatusCounts {
  inProgress: number;
  completed: number;
  notStarted: number;
}

export interface CourseListContextValue {
  courses: CourseStatusMap;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedStatus: CourseStatus;
  setSelectedStatus: (status: CourseStatus) => void;
  filteredCourses: CourseWithProgress[];
  statusCounts: StatusCounts;
  degreeId?: number;
}

export const CourseListContext = createContext<CourseListContextValue | undefined>(undefined);

interface CourseListProviderProps {
  courses: CourseWithProgress[];
  degreeId?: number;
  children: ReactNode;
}

export const CourseListProvider: FC<CourseListProviderProps> = ({
  courses: coursesData,
  degreeId,
  children,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<CourseStatus>('all');

  const courses = useMemo(() => {
    return coursesData.reduce<CourseStatusMap>(
      (acc, course) => {
        if (course.status) {
          acc[course.status].push(course);
        }
        return acc;
      },
      { all: coursesData, completed: [], inProgress: [], notStarted: [] }
    );
  }, [coursesData]);

  const statusCounts = useMemo(
    () => ({
      inProgress: courses.inProgress.length,
      completed: courses.completed.length,
      notStarted: courses.notStarted.length,
    }),
    [courses]
  );

  const filteredCourses = useMemo(() => {
    let filtered = courses[selectedStatus];

    if (searchQuery.trim()) {
      filtered = filtered.filter((course) =>
        course.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [courses, coursesData, searchQuery, selectedStatus]);

  const value: CourseListContextValue = {
    courses,
    searchQuery,
    setSearchQuery,
    selectedStatus,
    setSelectedStatus,
    filteredCourses,
    statusCounts,
    degreeId,
  };

  return <CourseListContext.Provider value={value}>{children}</CourseListContext.Provider>;
};

export const useCourseListContext = (): CourseListContextValue => {
  const context = useContext(CourseListContext);

  if (!context) {
    throw new Error('useCourseListContext must be used within CourseListProvider');
  }

  return context;
};
